import React, {useState} from 'react';
import {View, Alert} from 'react-native';
import {responsiveWidth} from 'react-native-responsive-dimensions';

import {text} from '../text';
import {theme} from '../constants';
import {components} from '../components';
import {homeIndicatorHeight} from '../utils';
import {PromocodeType} from '../types';
import {useFetchPromocodeMutation} from '../store/slices/apiSlice';

const Promocodes: React.FC = (): JSX.Element => {
  const indicatorHeight = homeIndicatorHeight();
  const [promocode, setPromocode] = useState('');
  const [codes, setCodes] = useState<PromocodeType[]>([]);
  const [fetchPromocode, {isLoading}] = useFetchPromocodeMutation();

  const handleAddPromocode = async () => {
    const trimmedCode = promocode.trim();

    if (!trimmedCode) {
      Alert.alert('Promocode', 'Enter a promocode to add.');
      return;
    }

    try {
      const data = await fetchPromocode({code: trimmedCode}).unwrap();
      setCodes((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.code.toLowerCase() === data.code.toLowerCase(),
        );

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = data;
          return updated;
        }

        return [data, ...prev];
      });
      setPromocode('');
      Alert.alert('Promocode added', 'The promocode was added to your wallet.');
    } catch (error: any) {
      const message =
        error?.data?.message ??
        error?.error ??
        'Promo code unavailable.';
      Alert.alert('Promocode', message);
    }
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return <components.Header goBack={true} title='Promocodes & gift cards' />;
  };

  const renderEmptyList = () => {
    if (codes.length === 0) {
      return (
        <components.KAScrollView
          contentContainerStyle={{
            flexGrow: 1,
            backgroundColor: theme.colors.backgroundPrimary,
            marginHorizontal: 20,
            borderRadius: 10,
            marginVertical: 10,
            paddingHorizontal: 20,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <components.Image
            source={{
              uri: 'https://george-fx.github.io/dine-hub/06.jpg',
            }}
            style={{
              width: responsiveWidth(100) - 80,
              aspectRatio: 1,
            }}
          />
          <text.H2
            style={{
              textAlign: 'center',
              marginBottom: 14,
            }}
          >
            You don’t have{'\n'}promocodes yet!
          </text.H2>
          <text.T16 style={{textAlign: 'center', marginBottom: 30}}>
            Stay tuned for and discounts to {'\n'}enhance your dining
            experience.
          </text.T16>
          <components.InputField
            type='promocode'
            placeholder='Add new coupon'
            value={promocode}
            onChangeText={setPromocode}
            containerStyle={{
              marginBottom: 20,
            }}
          />
          <components.Button
            title='add promocode'
            loading={isLoading}
            onPress={handleAddPromocode}
          />
        </components.KAScrollView>
      );
    }

    return null;
  };

  const renderAddPromocodeInput = () => {
    if (codes.length > 0) {
      return (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingBottom: indicatorHeight > 0 ? 10 : 20,
          }}
        >
          <components.InputField
            type='promocode'
            placeholder='Add new coupon'
            value={promocode}
            onChangeText={setPromocode}
            containerStyle={{
              width: '65%',
              marginRight: 10,
            }}
          />
          <components.Button
            title='+ add'
            containerStyle={{
              flex: 1,
              width: responsiveWidth(40),
            }}
            loading={isLoading}
            onPress={handleAddPromocode}
          />
        </View>
      );
    }

    return null;
  };

  const renderCodes = () => {
    if (codes.length === 0) {
      return null;
    }

    return (
      <View style={{marginBottom: 30}}>
        {codes.map((item, index) => {
          const isLast = index === codes.length - 1;
          return (
            <components.PromocodeItem
              item={item}
              key={item.id}
              isLast={isLast}
            />
          );
        })}
      </View>
    );
  };

  const renderContent = () => {
    if (codes.length > 0) {
      return (
        <components.KAScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: 20,
            paddingTop: 10,
          }}
        >
          {renderCodes()}
          {renderAddPromocodeInput()}
        </components.KAScrollView>
      );
    }

    return null;
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderEmptyList()}
      {renderContent()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Promocodes;
